import classNames from 'classnames';
import {
  useParams,
  useSearchParams
} from 'react-router-dom';
import { remToPx, Box, Inline, Title } from 'src/viewLayer/components/DesignSystem';

import DisplayLanguageSelectionBox from 'src/viewLayer/components/shared/DisplayLanguageSelectionBox';
import { t } from 'src/viewLayer/helpers/translations';
import { buildValidFlowPath } from 'src/viewLayer/helpers/pathHelpers';
import { getLanguageSelectionList } from 'src/viewLayer/helpers/languageSelection';
import { PolishInformationBox } from 'src/viewLayer/components/shared/PolishInformationBox';
import FlagComponent from './FlagComponent';
import { trackGuiInteracted } from 'src/dataLayer/remoteData/babbelEventStream';
import { LOCALE } from 'src/dataLayer/env';
import type { LanguageSelectionProps } from '../types';
import { PageContainer, HeaderContainer, LanguageContainer } from './index.styled';
import { RadioInput } from 'src/viewLayer/components/shared/RadioInput/index.styled';

export default (
  { nodeConfig: { variables = {}, options = {} } }: LanguageSelectionProps
): JSX.Element => {
  const { flowId, ...pathParams } = useParams<{flowId?: string}>();
  const [queryParams,] = useSearchParams();
  // use LOCALE as locale for building redirect urls
  const updatedPathParams = { ...pathParams, flowId, locale: LOCALE };
  const { title, info_box, redirect_flow: targetFlow } = variables;
  const { hideDisplayLanguageDropdown = false } = options;
  const isLiveFlow = flowId.endsWith('_live');
  const languageSelectionList = getLanguageSelectionList(isLiveFlow);
  const redirectionParams = targetFlow
    ? { ...updatedPathParams, flowId: targetFlow, nodeNumber: 0 }
    : updatedPathParams;
  const handleClickLanguage = (learnLang: string): void => {
    trackGuiInteracted({ learnLang, guiElementName: `flag_${learnLang}` });
    window.location.assign(
      buildValidFlowPath({ ...redirectionParams, learnLang, queryParams })
    );
  };

  // TODO: move uat-language_selection id from the div back to the LanguageContainer when DS team fix the bug with ids
  // not being passed to Card components in the rebranded Card component
  return (
    <div id="uat-language_selection" className="PageNode LanguageSelection">
      <PageContainer id="PageContainer" align="center" alignItems="center">
        <HeaderContainer>
          <Title size={{ xxxsmall: 'small', medium: 'default' }} textAlign="center">{t(title.key)}</Title>
        </HeaderContainer>
        <Box className="languageListContainer">
          {
            languageSelectionList.map((language) =>
              <LanguageContainer
                key={ language.learnLanguage }
                data-testid={`language-${language.learnLanguage}`}
                className={ classNames('languageContainer', { isLiveFlow }) }
                onClick={ (): void => handleClickLanguage(language.learnLanguage) }
              >
                <Inline gap={ remToPx(0.75) } alignItems="center">
                  <div id={`uat-language-selection-${language.learnLanguage}`} className="flag small">
                    <FlagComponent flag={ language.flag } />
                  </div>
                  <span className="label">{ language.learnLanguageTranslation }</span>
                </Inline>
                <RadioInput />
              </LanguageContainer>
            )
          }
        </Box>
        { LOCALE === 'pl' &&
          <PolishInformationBox
            title={ t(info_box.title.key) }
            infoBoxContentKeyPart1={ info_box.content.key_interpolated1 }
            infoBoxContentKeyPart2={ info_box.content.key_interpolated2 }
          />
        }
        { !hideDisplayLanguageDropdown && <DisplayLanguageSelectionBox /> }
      </PageContainer>
    </div>
  );
};
